/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Inviting the team, one line per person, grouped by the plan that pays.
 *
 * A block per plan rather than one list: the plan sizes what the person gets,
 * so the seat somebody is invited into is a choice, not a detail. The blocks
 * come off the subscription — a plan with every seat taken says so and offers
 * no row, instead of collecting an address that could never be sent.
 *
 * The body is kept apart from the route so §8's invite modal can mount the
 * same rows without the page around them.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text, UnstyledButton } from '@/shared/ui';
import { OwnerSeatPick } from '@/features/subscription/components/seats/OwnerSeatPick';
import { SeatPlanCompare } from '@/features/subscription/components/seats/SeatPlanCompare';
import type { PlanFeatureType } from '@/features/pricing';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import type {
  InviteEntry, InviteVerdict, TeamSeatBlock,
} from '@/services/requests/housien-services/AgenciesService.team';
import {
  AddRowButton, BlockHeader, InviteRow, PersonIcon, ROLE_WIDTH,
} from './InviteTeam.parts';
import {
  SUPPORT_BLOCK_ID,
  defaultRoleFor,
  rolesFor,
  allRowsOf, applyPaste, canAddRow, freeTotal, openingRows, readyCount,
  rowError, sendableEntries, splitPasted,
  type RowDraft, type RowError, type RowsByPlan,
} from './InviteTeam.rows';

export interface InviteTeamProps {
  /** One block per plan the subscription bought. */
  blocks: TeamSeatBlock[];
  /** The plan's display name, or null when the catalogue does not know it. */
  planTitle: (planId: string) => string | null;
  /**
   * Resolves the catalogue plan behind a seat block, for the comparison.
   *
   * Optional: a caller that cannot reach the catalogue simply shows the
   * three names, which is what this screen did before.
   */
  planFor?: (planId: string) => PlanFeatureType | null;
  /** Everybody already on the team, so they are not invited twice. */
  seatedEmails: string[];
  /** The plan whose seat the owner occupies, for the notice. */
  ownerPlanTitle?: string | null;
  /**
   * Seats the owner under the chosen plan. Offered while the owner has no
   * seat of their own — the buyer picks which of the bought seats is theirs.
   */
  onChooseOwnerSeat?: (planId: string) => Promise<boolean>;
  /** Sends the batch and reports back what happened to each line. */
  onSend: (entries: InviteEntry[]) => Promise<InviteVerdict[]>;
  /** Leaves without inviting. */
  onSkip: () => void;
  /** Opens the plan tab, where seats are bought. */
  onGoToPlan?: () => void;
  /** The modal in §8 drops the page's title and note. */
  compact?: boolean;
}

/** The footer's pill buttons, which the size tokens do not round this far. */
const PILL: React.CSSProperties = { height: 44, borderRadius: 'var(--radius-full)' };

/** Why a line came back refused, keyed by the reason the batch gave. */
const REFUSALS: Record<string, RowError> = {
  invalid: 'invalid',
  duplicate: 'duplicate',
  already_member: 'already_member',
  already_invited: 'already_member',
  no_seats: 'no_seats',
  plan_full: 'no_seats',
  send_failed: 'send_failed',
};

/**
 * The invite body: the blocks, their rows, and the footer that sends them.
 *
 * @param props - The seats, the team, and what to do with the batch.
 * @returns The invite form.
 */
export const InviteTeam: React.FC<InviteTeamProps> = ({
  blocks, planTitle, planFor, seatedEmails, ownerPlanTitle,
  onChooseOwnerSeat, onSend, onSkip, onGoToPlan, compact = false,
}) => {
  const { t } = useTranslation('@pages/onboarding');
  const isMobile = useMediaQuery('(max-width: 639px)');
  const [rows, setRows] = useState<RowsByPlan>({});
  const [showErrors, setShowErrors] = useState(false);
  const [flash, setFlash] = useState('');
  const [sending, setSending] = useState(false);
  const [sendErrors, setSendErrors] = useState<Record<string, RowError>>({});
  const seq = useRef(0);

  const makeRow = useCallback((email = '', role = defaultRoleFor('')): RowDraft => {
    seq.current += 1;
    return { id: `invite-row-${seq.current}`, email, role };
  }, []);

  // A block opens with one empty line, unless every seat in it is taken. The
  // opening row is derived rather than seeded, so a block that fills up while
  // the screen is open stops offering a row it can no longer send.
  const rowsOf = useCallback((block: TeamSeatBlock): RowDraft[] => (
    rows[block.plan_id] ?? openingRows(block, {
      id: `seed-${block.plan_id}`, email: '', role: defaultRoleFor(block.plan_id),
    })
  ), [rows]);

  const write = useCallback((planId: string, next: (current: RowDraft[]) => RowDraft[]) => {
    setRows((current) => {
      const block = blocks.find((candidate) => candidate.plan_id === planId);
      const existing = current[planId]
        ?? (block ? openingRows(block, {
          id: `seed-${planId}`, email: '', role: defaultRoleFor(planId),
        }) : []);
      return { ...current, [planId]: next(existing) };
    });
  }, [blocks]);

  const everyRow = useMemo(
    () => blocks.flatMap((block) => rowsOf(block)),
    [blocks, rowsOf],
  );
  const ready = readyCount(blocks, Object.fromEntries(
    blocks.map((block) => [block.plan_id, rowsOf(block)]),
  ));
  const free = freeTotal(blocks);
  // The owner holds a paid seat, and the plans compared are the ones paid
  // for: the auxiliary block is no plan and has no allowance to offer.
  const paidBlocks = useMemo(
    () => blocks.filter((block) => block.plan_id !== SUPPORT_BLOCK_ID),
    [blocks],
  );

  const errorOf = (row: RowDraft): RowError => {
    if (sendErrors[row.id]) return sendErrors[row.id];
    if (!showErrors && !(row.email && row.touched)) return '';
    return rowError(row.email, everyRow, seatedEmails);
  };

  const addRow = (planId: string) => write(
    planId, (current) => [...current, makeRow('', defaultRoleFor(planId))],
  );

  const send = async () => {
    const drafts = Object.fromEntries(
      blocks.map((block) => [block.plan_id, rowsOf(block)]),
    );
    const typed = allRowsOf(blocks, drafts).filter((row) => row.email.trim());
    if (!typed.length || sending) return;
    if (typed.some((row) => rowError(row.email, everyRow, seatedEmails))) {
      setShowErrors(true);
      return;
    }
    setSending(true);
    try {
      const verdicts = await onSend(sendableEntries(blocks, drafts));
      // A refusal the screen could not have known about — the seat taken
      // between loading and sending, the mail server having a bad moment —
      // comes back onto the line that caused it instead of a bare failure.
      const refused = verdicts.filter((verdict) => !verdict.ok);
      setSendErrors(Object.fromEntries(refused.map((verdict) => {
        const row = typed.find((draft) => draft.email.trim().toLowerCase() === verdict.email);
        return [row?.id ?? verdict.email, REFUSALS[verdict.reason ?? ''] ?? 'send_failed'];
      })));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {!compact && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <Text
              component="h1"
              style={{
                margin: 0, fontFamily: 'var(--font-family-alt-2)',
                fontSize: 'var(--text-app-title)', fontWeight: 'var(--font-bold)',
                letterSpacing: '-0.02em', color: 'var(--text-primary)',
              }}
            >
              {t('inviteTeam.title')}
            </Text>
            <Text
              style={{
                margin: 0, fontSize: 'var(--text-sm)', lineHeight: 1.5,
                color: 'var(--text-secondary)', textWrap: 'pretty',
              }}
            >
              {t('inviteTeam.subtitle')}
            </Text>
          </div>

          {ownerPlanTitle && (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
                padding: 'var(--space-2-5) var(--space-3-5)',
                borderRadius: 'var(--radius-lg)', background: 'var(--surface-raised)',
                border: '1px solid var(--border-subtle)',
                fontSize: 'var(--text-small)', lineHeight: 1.4,
                color: 'var(--seed-base)',
              }}
            >
              <PersonIcon />
              <Text component="span" style={{ color: 'var(--text-primary)' }}>
                {t('inviteTeam.ownerNotice', { plan: ownerPlanTitle })}
              </Text>
            </div>
          )}

          {/* The buyer is seat one, but nothing picked which of the bought
              seats is theirs — their allowance depends on it, so they say. */}
          {!ownerPlanTitle && onChooseOwnerSeat && paidBlocks.length > 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <OwnerSeatPick
                blocks={paidBlocks}
                planTitle={planTitle}
                onChoose={onChooseOwnerSeat}
              />
              {/* The three names mean nothing to someone who has not used the
                  product yet, and this screen is their first. */}
              {planFor && <SeatPlanCompare blocks={paidBlocks} planFor={planFor} />}
            </div>
          )}
        </>
      )}

      <div
        style={{
          display: 'flex', flexDirection: 'column', background: 'var(--surface-raised)',
          border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
        }}
      >
        {blocks.map((block, position) => {
          const blockRows = rowsOf(block);
          const support = block.plan_id === SUPPORT_BLOCK_ID;
          // The auxiliary block belongs to no plan, so the catalogue has no
          // name to give it.
          const title = support ? null : planTitle(block.plan_id);
          const full = block.free <= 0;
          const left = Math.max(0, block.free - blockRows.filter((r) => r.email.trim()).length);
          return (
            <div
              key={block.plan_id}
              data-testid={`invite-block-${block.plan_id}`}
              style={{
                display: 'flex', flexDirection: 'column', gap: 'var(--space-2-5)',
                padding: 'var(--space-4) var(--space-5)',
                borderTop: position === 0 ? 'none' : '1px solid var(--border-ultra-subtle)',
              }}
            >
              <BlockHeader
                title={support
                  ? t('inviteTeam.supportBlockTitle')
                  : (title
                    ? t('inviteTeam.blockTitle', { plan: title })
                    : t('inviteTeam.blockTitleUnknown'))}
                free={full
                  ? t('inviteTeam.allTaken')
                  : t('inviteTeam.freeOf', { free: left, bought: block.bought })}
                full={full || left <= 0}
                badge={support ? t('inviteTeam.supportBadge') : null}
              />
              {support ? (
                <Text
                  component="span"
                  style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}
                >
                  {t('inviteTeam.supportBlockNote')}
                </Text>
              ) : null}
              {blockRows.map((row, index) => (
                <InviteRow
                  key={row.id}
                  row={row}
                  index={index}
                  planId={block.plan_id}
                  error={errorOf(row)}
                  roleWidth={isMobile ? ROLE_WIDTH.mobile : ROLE_WIDTH.desktop}
                  roles={rolesFor(block.plan_id)}
                  onEmail={(value) => write(block.plan_id, (current) => current.map(
                    (candidate) => (candidate.id === row.id
                      ? { ...candidate, email: value, touched: true } : candidate),
                  ))}
                  onRole={(value) => write(block.plan_id, (current) => current.map(
                    (candidate) => (candidate.id === row.id
                      ? { ...candidate, role: value } : candidate),
                  ))}
                  onRemove={() => write(block.plan_id, (current) => {
                    const kept = current.filter((candidate) => candidate.id !== row.id);
                    return kept.length ? kept : [makeRow('', defaultRoleFor(block.plan_id))];
                  })}
                  onPaste={(text) => write(block.plan_id, (current) => {
                    const result = applyPaste(
                      current, row.id, splitPasted(text), block.free, makeRow,
                    );
                    setFlash(result.overflow
                      ? t('inviteTeam.pasteOverflow', {
                        count: result.overflow,
                        plan: title ?? t('inviteTeam.blockTitleUnknown'),
                      })
                      : '');
                    return result.rows;
                  })}
                  onEnter={() => {
                    const last = blockRows[blockRows.length - 1];
                    if (last?.id === row.id && row.email.trim() && canAddRow(block, blockRows)) {
                      addRow(block.plan_id);
                    }
                  }}
                />
              ))}
              {canAddRow(block, blockRows) && (
                <AddRowButton planId={block.plan_id} onClick={() => addRow(block.plan_id)} />
              )}
              {full && (
                <Text
                  component="span"
                  style={{
                    fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)',
                  }}
                >
                  {t('inviteTeam.planFull', {
                    plan: title ?? t('inviteTeam.blockTitleUnknown'),
                  })}
                </Text>
              )}
            </div>
          );
        })}
      </div>

      {flash && (
        <Text
          role="status"
          style={{ fontSize: 'var(--text-meta)', color: 'var(--accent-mostarda)' }}
        >
          {flash}
        </Text>
      )}

      <div
        style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          justifyContent: 'space-between', gap: 'var(--space-3)',
        }}
      >
        <Text style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}>
          {ready
            ? t('inviteTeam.readyCount', { ready, free })
            : t('inviteTeam.freeToInvite', { free })}
        </Text>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            marginLeft: 'auto',
          }}
        >
          <Button variant="ghost" size="md" onClick={onSkip} style={PILL}>
            {compact ? t('inviteTeam.cancel') : t('inviteTeam.later')}
          </Button>
          <Button
            variant="solid"
            size="md"
            data-testid="invite-send"
            onClick={send}
            disabled={!ready || sending}
            style={PILL}
          >
            {t('inviteTeam.send', { count: ready })}
          </Button>
        </div>
      </div>

      {!compact && (
        <Text
          style={{
            /* The last line on the screen sat against the bottom edge, which
               reads as a page that was cut rather than one that ended. */
            margin: '0 0 var(--space-10)',
            fontSize: 'var(--text-meta)', lineHeight: 1.5,
            color: 'var(--text-tertiary-soft)',
          }}
        >
          {t('inviteTeam.note')}
          {onGoToPlan && (
            <>
              {' '}
              <UnstyledButton
                onClick={onGoToPlan}
                style={{
                  padding: 0, background: 'none', cursor: 'pointer',
                  color: 'var(--link-text)', textDecoration: 'underline',
                  textUnderlineOffset: 3, fontSize: 'inherit', fontFamily: 'inherit',
                }}
              >
                {t('inviteTeam.noteLink')}
              </UnstyledButton>
            </>
          )}
        </Text>
      )}
    </div>
  );
};
